import * as React from "react";
import { useState, useEffect } from "react";
import { Dropdown, Button, Segment, Message, Loader, Icon, DropdownProps } from "semantic-ui-react";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-markdown";
import "ace-builds/src-noconflict/theme-monokai";
import "ace-builds/src-noconflict/theme-github";

import { backend } from "../../../../../xconvert-backend";
import { authentication } from "../../../../../authentication";
import { ScriptsDocumentationEntry } from "../../../../../generated/api";
import { notify } from 'react-notify-toast';

type DocumentationEnumType = ScriptsDocumentationEntry.DocumentationTypeEnum;

const getDocumentationTypeOptions = () => {
    return Object.values(ScriptsDocumentationEntry.DocumentationTypeEnum).map(enumValue => {
        let text = enumValue
            .replace(/^GET_/, '')
            .replace(/_DOCUMENTATION$/, '')
            .replace(/_/g, ' ')
            .toLowerCase(); // Convert to lower case

        text = text
            .split(' ')
            .map(word => word ? word.charAt(0).toUpperCase() + word.slice(1) : '')
            .join(' ');

        if (!text.toLowerCase().endsWith("documentation")) {
            text += " Documentation";
        }


        return {
            key: enumValue,
            text: text.trim(),
            value: enumValue as DocumentationEnumType,
        };
    });
};

const documentationTypeOptions = getDocumentationTypeOptions();

export function DocumentationEditor() {
    const [selectedDocType, setSelectedDocType] = useState<DocumentationEnumType | null>(
        documentationTypeOptions.length > 0 ? documentationTypeOptions[0].value : null
    );
    const [documentationContent, setDocumentationContent] = useState<string>("");
    const [originalDocumentationContent, setOriginalDocumentationContent] = useState<string>("");
    const [docId, setDocId] = useState<string | null>(null);
    const [lastUpdateDate, setLastUpdateDate] = useState<string | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isDarkTheme, setIsDarkTheme] = useState<boolean>(true);

    const fetchDocumentation = async (docType: DocumentationEnumType | null) => {
        if (!docType) {
            setDocumentationContent("");
            setOriginalDocumentationContent("");
            setDocId(null);
            setLastUpdateDate(null);
            // setSelectedDocType(null); // Not needed here, already handled by caller or initial state
            return;
        }
        setIsLoading(true);
        setDocumentationContent(""); // Clear previous content for better UX
        setOriginalDocumentationContent("");
        setDocId(null);
        setLastUpdateDate(null);

        try {
            const auth = backend.withTokenAuthHeader(authentication.token);
            const response = await backend.internalApi.fetchScriptDocumentationByDocumentationType(docType, auth);

            if (response) {
                setDocumentationContent(response.content || "");
                setOriginalDocumentationContent(response.content || "");
                setDocId(response._id || null);
                setLastUpdateDate(response.latestUpdateDate ? new Date(response.latestUpdateDate).toLocaleString() : "N/A");
                // notify.show(`Documentation for ${docType.replace(/_/g, ' ')} loaded.`, "success", 2000);
            } else {
                notify.show(`No documentation found for ${docType.replace(/_/g, ' ')}. You can create a new one.`, "info", 3000);
            }
        } catch (error: any) {
            console.error("Error fetching documentation:", error);
            const readableDocType = docType.replace(/_/g, ' ');
            if (error.response && error.response.status === 404) {
                notify.show(`No documentation found for ${readableDocType}. You can create a new one.`, "info", 3000);
            } else {
                notify.show(`Failed to fetch documentation for ${readableDocType}: ${error.message || 'Unknown error'}`, "error", 3000);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleDocTypeChange = (_: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
        const newDocType = data.value as DocumentationEnumType;
        setSelectedDocType(newDocType);
        if (newDocType) {
            fetchDocumentation(newDocType);
        } else { // If dropdown is cleared
            setDocumentationContent("");
            setOriginalDocumentationContent("");
            setDocId(null);
            setLastUpdateDate(null);
        }
    };

    const handleReload = () => {
        if (selectedDocType) {
            fetchDocumentation(selectedDocType);
        } else {
            notify.show("Please select a documentation type first.", "warning", 3000);
        }
    };

    const handleSave = async () => {
        if (!selectedDocType) {
            notify.show("Cannot save without a selected documentation type.", "error", 3000);
            return;
        }
        setIsSaving(true);
        try {
            const auth = backend.withTokenAuthHeader(authentication.token);
            const entry: ScriptsDocumentationEntry = {
                _id: docId,
                documentationType: selectedDocType,
                content: documentationContent,
            };

            const savedEntry = await backend.internalApi.createOrModifyScriptDocumentation(entry, auth);

            if (savedEntry) {
                setOriginalDocumentationContent(savedEntry.content || "");
                setDocumentationContent(savedEntry.content || "");
                setDocId(savedEntry._id || null);
                setLastUpdateDate(savedEntry.latestUpdateDate ? new Date(savedEntry.latestUpdateDate).toLocaleString() : "N/A");
                notify.show("Documentation saved successfully!", "success", 2000);
            } else {
                throw new Error("Save operation did not return the saved entry.");
            }
        } catch (error: any) {
            console.error("Error saving documentation:", error);
            notify.show(`Failed to save documentation: ${error.message || 'Unknown error'}`, "error", 3000);
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        if (selectedDocType) {
            fetchDocumentation(selectedDocType);
        }
    }, []);

    const hasChanges = documentationContent !== originalDocumentationContent;
    const currentTheme = isDarkTheme ? "monokai" : "github";
    const selectedDocTypeText = documentationTypeOptions.find(opt => opt.value === selectedDocType)?.text || selectedDocType?.replace(/_/g, ' ') || "N/A";


    return (
        <Segment style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '1em' }}>
            <div style={{display: "flex", alignItems: "center", gap: "2px", flexWrap: 'wrap' }}>
                <Dropdown
                    placeholder="Select Documentation Type"
                    selection
                    options={documentationTypeOptions}
                    value={selectedDocType}
                    onChange={handleDocTypeChange}
                    disabled={isLoading || isSaving}
                    style={{flex: '1 1 300px', minWidth: '250px'}}
                    clearable // Allows deselecting
                />
                <div style={{display: "flex", gap: "5px", flexWrap: 'nowrap'}}>
                    <Button icon onClick={() => setIsDarkTheme(!isDarkTheme)} title={isDarkTheme ? "Light Theme" : "Dark Theme"} disabled={isLoading || isSaving}>
                        <Icon name={isDarkTheme ? "sun" : "moon"} />
                    </Button>
                    <Button onClick={handleReload}
                            disabled={isLoading || isSaving || !selectedDocType}
                            loading={isLoading && !isSaving}
                            icon
                            title="Reload Documentation">
                        <Icon name="refresh" />
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={isLoading || isSaving || !hasChanges || !selectedDocType}
                        loading={isSaving}
                        primary
                        icon
                        labelPosition="left"
                        title="Save Changes"
                    >
                        <Icon name="save" />
                        Save
                    </Button>
                </div>
            </div>

            {selectedDocType && (lastUpdateDate || isLoading || docId) && (
                <Message size="small" style={{color: 'black', marginBottom: '5px'}}>
                    {isLoading && !isSaving ? <p><strong>Status:</strong> Loading...</p> : (lastUpdateDate && <p><strong>Last Updated:</strong> {lastUpdateDate}</p>)}
                    {docId && <p><strong>ID:</strong> {docId}</p>}
                </Message>
            )}

            <div style={{
                flexGrow: 1,
                display: 'flex',
                minHeight: '300px',
                border: '1px solid rgba(34,36,38,.15)',
                borderRadius: '.28571429rem'
            }}>
                {isLoading && !isSaving && (
                    <div style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Loader active inline="centered" size="large">Loading documentation...</Loader>
                    </div>
                )}

                {!isLoading && selectedDocType && (
                    <AceEditor
                        mode="markdown"
                        theme={currentTheme}
                        value={documentationContent}
                        onChange={setDocumentationContent}
                        name="documentationEditor"
                        editorProps={{ $blockScrolling: true }}
                        width="100%"
                        height="100%"
                        fontSize={14}
                        showPrintMargin={false}
                        showGutter={true}
                        highlightActiveLine={true}
                        setOptions={{
                            useWorker: false,
                            enableBasicAutocompletion: true,
                            enableLiveAutocompletion: true,
                            enableSnippets: true,
                        }}
                    />
                )}
                {!isLoading && !selectedDocType && (
                    <Message info style={{color: 'black', width: '100%', textAlign: 'center', alignSelf: 'center', margin: 0, boxShadow: 'none', border: 'none' }}>
                        <Message.Header>No Documentation Type Selected</Message.Header>
                        <p>Please select a documentation type from the dropdown to view or edit its content.</p>
                    </Message>
                )}
            </div>
        </Segment>
    );
}